import { useEffect } from "react";

/** Callback triggered when document visibility changes */
type VisibilityChangeCallback = (isVisible: boolean) => void;

/**
 * Hook that reacts to document visibility changes.
 * Calls the provided callback when the page becomes visible or hidden.
 *
 * @param callback - Function called with true when page becomes visible, false when hidden
 *
 * @example
 * useVisibilityChange((isVisible) => {
 *   if (isVisible) {
 *     console.log("Page is now visible");
 *   } else {
 *     console.log("Page is now hidden");
 *   }
 * });
 */
export function useVisibilityChange(callback: VisibilityChangeCallback): void {
	useEffect(() => {
		const handleVisibilityChange = () => {
			callback(!document.hidden);
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [callback]);
}
